"use client"

import { create } from "zustand"
import { toast } from "sonner"
import type { QuotesState, QuotesActions } from "./types"
import {
  createQuoteApi,
  getQuotesApi,
  getQuoteByIdApi,
  updatePricingOverridesApi,
  updatePricingOverridesWithPdfApi,
  updateCustomerInterestApi,
} from "./api"

const initialState: QuotesState = {
  quote: null,
  quotes: [],
  isLoading: false,
  error: null,
}

export const useQuotesStore = create<QuotesState & QuotesActions>()((set) => ({
  ...initialState,

  createQuote: async (payload) => {
    set({ isLoading: true, error: null })
    try {
      const res = await createQuoteApi(payload)
      set({ quote: res.data, isLoading: false })
      return res.data
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ??
        "Failed to generate quote. Please try again."
      set({ isLoading: false, error: message })
      toast.error(message)
      throw err
    }
  },

  fetchQuotes: async () => {
    set({ isLoading: true, error: null })
    try {
      const res = await getQuotesApi()
      set({ quotes: res.data, isLoading: false })
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message = apiError?.error?.message ?? "Failed to fetch quotes."
      set({ isLoading: false, error: message })
      toast.error(message)
    }
  },

  fetchQuoteById: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const res = await getQuoteByIdApi(id)
      set({ quote: res.data, isLoading: false })
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message = apiError?.error?.message ?? "Failed to fetch quote."
      set({ isLoading: false, error: message })
      toast.error(message)
    }
  },

  clearQuote: () => set({ quote: null }),
  clearError: () => set({ error: null }),
  reset: () => set(initialState),

  updatePricingOverrides: async (quoteId, payload) => {
    set({ isLoading: true, error: null })
    try {
      const res = await updatePricingOverridesApi(quoteId, payload)
      set({ quote: res.data, isLoading: false })
      return res.data
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to update pricing overrides."
      set({ isLoading: false, error: message })
      throw err
    }
  },

  updatePricingOverridesWithPdf: async (quoteId, payload) => {
    set({ isLoading: true, error: null })
    try {
      const res = await updatePricingOverridesWithPdfApi(quoteId, payload)
      set({ quote: res.data, isLoading: false })
      return res.data
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ??
        "Failed to update pricing overrides with PDF."
      set({ isLoading: false, error: message })
      throw err
    }
  },

  updateCustomerInterest: async (quoteId: string, isInterested: boolean) => {
    set({ isLoading: true, error: null })
    try {
      const res = await updateCustomerInterestApi(quoteId, isInterested)
      set({ quote: res.data, isLoading: false })
      toast.success(
        isInterested
          ? "Customer marked as interested"
          : "Customer interest updated"
      )
      return res.data
    } catch (err: unknown) {
      const apiError = err as { error?: { message?: string } }
      const message =
        apiError?.error?.message ?? "Failed to update customer interest."
      set({ isLoading: false, error: message })
      toast.error(message)
      throw err
    }
  },
}))

export type {
  QuotesState,
  QuotesActions,
  Quote,
  CreateQuotePayload,
  PricingOverridePayload,
} from "./types"
